<template>
  <el-dialog
    :visible.sync="visible"
    :show-close="false"
    :close-on-click-modal="true"
    :close-on-press-escape="true"
    append-to-body
    custom-class="global-apply-dialog"
    @closed="resetForm"
  >
    <div class="dialog-body">
      <div class="dialog-title-row">
		<img class="sparkle left" :src="sparkleImg" alt="" />
        <h2>立即申请</h2>
		<img class="sparkle right" :src="sparkleImg" alt="" />
      </div>

      <el-form ref="applyForm" :model="form" :rules="rules" label-position="top" class="apply-form">
        <el-form-item label="您的称呼" prop="name">
          <el-input v-model.trim="form.name" maxlength="20" placeholder="请输入您的称呼" class="dialog-control" />
        </el-form-item>

        <el-form-item label="联系电话" prop="phone">
          <el-input v-model.trim="form.phone" maxlength="11" placeholder="请输入联系电话" class="dialog-control" />
        </el-form-item>

        <el-form-item label="角色类型" prop="roleType">
          <el-select v-model="form.roleType" placeholder="请选择角色类型" class="dialog-control">
            <el-option v-for="option in roleOptions" :key="option.value" :label="option.label" :value="option.value" />
          </el-select>
        </el-form-item>

        <el-form-item label="所属机构">
          <el-input v-model.trim="form.organization" maxlength="50" placeholder="选填，如无可不填" class="dialog-control" />
        </el-form-item>

        <el-form-item label="需求类型（可多选）" prop="demandTypes">
          <el-checkbox-group v-model="form.demandTypes" class="demand-group">
            <el-checkbox v-for="option in demandOptions" :key="option.value" :label="option.value">{{ option.label }}</el-checkbox>
          </el-checkbox-group>
        </el-form-item>

        <el-form-item label="需求说明" prop="description">
          <el-input
            v-model.trim="form.description"
            type="textarea"
            resize="none"
            maxlength="100"
            :rows="4"
            placeholder="请简要描述您的需求，输入内容100个字以内"
            class="dialog-textarea"
          />
        </el-form-item>

        <el-form-item label="图形验证码" prop="captchaCode">
          <div class="captcha-row">
            <el-input v-model.trim="form.captchaCode" maxlength="6" placeholder="请输入验证码" class="dialog-control captcha-input" />
            <button type="button" class="captcha-box" @click="refreshCaptcha" title="点击刷新验证码">
              <img v-if="captchaImage" :src="captchaImage" alt="图形验证码" class="captcha-image" />
              <span v-else>加载中</span>
            </button>
          </div>
        </el-form-item>
      </el-form>

      <button type="button" class="submit-btn" :disabled="submitting" @click="submitForm">{{ submitting ? '提交中...' : '提交申请' }}</button>
    </div>
  </el-dialog>
</template>

<script>
import { getCaptcha, submitCooperationApplication } from '@/api/user';
import { applyDialogBus } from '@/utils/applyDialog';
import sparkleImg from '@/assets/img/zs1.png';

function createEmptyForm() {
  return {
    name: '',
    phone: '',
    roleType: '',
    organization: '',
    demandTypes: [],
    description: '',
    captchaCode: '',
    source: ''
  };
}

const roleOptions = [
  { value: 'individual', label: '个人主播' },
  { value: 'organization', label: 'MCN机构' }
];

const demandOptions = [
  { value: 'support', label: '我要扶持' },
  { value: 'training', label: '我要培训' },
  { value: 'order', label: '我要接单' },
  { value: 'funding', label: '我要资金' },
  { value: 'asset_evaluation', label: '我要资产评估' }
];

const roleLabelToValueMap = roleOptions.reduce((map, option) => {
  map[option.label] = option.value;
  map[option.value] = option.value;
  return map;
}, {});

const demandLabelToValueMap = demandOptions.reduce((map, option) => {
  map[option.label] = option.value;
  map[option.value] = option.value;
  return map;
}, {});

function normalizeRoleType(roleType) {
  return roleLabelToValueMap[roleType] || '';
}

function normalizeDemandTypes(demandTypes) {
  if (!Array.isArray(demandTypes)) {
    return [];
  }

  return demandTypes
    .map(type => demandLabelToValueMap[type])
    .filter(Boolean);
}

export default {
  name: 'GlobalApplyDialog',
  data() {
    const validatePhone = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请输入联系电话'));
        return;
      }
      if (!/^1[3-9]\d{9}$/.test(value)) {
        callback(new Error('请输入正确的联系电话'));
        return;
      }
      callback();
    };

    return {
      visible: false,
      submitting: false,
      captchaKey: '',
      captchaImage: '',
      sparkleImg,
      form: createEmptyForm(),
      roleOptions,
      demandOptions,
      rules: {
        name: [{ required: true, message: '请输入您的称呼', trigger: 'blur' }],
        phone: [{ required: true, validator: validatePhone, trigger: 'blur' }],
        roleType: [{ required: true, message: '请选择角色类型', trigger: 'change' }],
        demandTypes: [{ type: 'array', required: true, message: '请选择需求类型', trigger: 'change' }],
        description: [{ required: true, message: '请输入需求说明', trigger: 'blur' }],
        captchaCode: [{ required: true, message: '请输入图形验证码', trigger: 'blur' }]
      }
    };
  },
  created() {
    applyDialogBus.$on('open', this.handleOpen);
  },
  beforeDestroy() {
    applyDialogBus.$off('open', this.handleOpen);
  },
  methods: {
    async handleOpen(payload = {}) {
      this.visible = true;
      this.form = {
        ...createEmptyForm(),
        ...payload,
        roleType: normalizeRoleType(payload.roleType),
        demandTypes: normalizeDemandTypes(payload.demandTypes)
      };
      this.$nextTick(() => {
        if (this.$refs.applyForm) {
          this.$refs.applyForm.clearValidate();
        }
      });
      await this.refreshCaptcha(true);
    },
    resetForm() {
      this.submitting = false;
      this.captchaKey = '';
      this.captchaImage = '';
      this.form = createEmptyForm();
      if (this.$refs.applyForm) {
        this.$refs.applyForm.resetFields();
      }
    },
    async refreshCaptcha(silent = false) {
      try {
        const response = await getCaptcha();
        this.captchaKey = response && response.key ? response.key : '';
        this.captchaImage = response && response.img ? response.img : '';
      } catch (error) {
        this.captchaKey = '';
        this.captchaImage = '';
        if (!silent) {
          this.$message.warning(error && error.message ? error.message : '图形验证码加载失败');
        }
      }
    },
    isCaptchaError(error) {
      const errors = error && error.data && error.data.errors;
      return Boolean(errors && errors.captcha_code)
        || (error && error.message ? error.message.includes('图形验证码') : false);
    },
    isRateLimitError(error) {
      const status = error && error.response ? error.response.status : null;
      const message = error && error.message ? error.message : '';
      return status === 429 || message === 'Too Many Attempts.';
    },
    buildSubmitPayload() {
      return {
        name: this.form.name,
        phone: this.form.phone,
        role_type: this.form.roleType,
        organization: this.form.organization,
        demand_types: this.form.demandTypes,
        description: this.form.description,
        captcha_key: this.captchaKey,
        captcha_code: this.form.captchaCode
      };
    },
    submitForm() {
      if (!this.captchaKey || !this.captchaImage) {
        this.$message.warning('图形验证码加载失败，请刷新后重试');
        return;
      }

      this.$refs.applyForm.validate(async valid => {
        if (!valid || this.submitting) {
          return;
        }

        this.submitting = true;
        try {
          const response = await submitCooperationApplication(this.buildSubmitPayload());
          this.$message.success((response && response.message) || '申请已提交');
          this.visible = false;
        } catch (error) {
          const message = this.isRateLimitError(error)
            ? '提交次数过多，请稍后再试'
            : (error && error.message ? error.message : '申请提交失败');
          this.$message.warning(message);
          if (this.isCaptchaError(error)) {
            this.form.captchaCode = '';
            await this.refreshCaptcha(true);
          }
        } finally {
          this.submitting = false;
        }
      });
    }
  }
};
</script>

<style lang="scss">
.global-apply-dialog {
  width: 646px !important;
  height: auto;
  max-height: calc(100vh - 32px);
  background: #ffffff;
  box-shadow: 2px 2px 10px 0 rgba(164, 177, 238, 0.3), 0 0 1px 0 rgba(0, 0, 0, 0.08) !important;
  border-radius: 20px !important;
  border: 1px solid #e2e8f0 !important;
  margin-top: 4vh !important;
  overflow: hidden;
}
.el-dialo{
	width: 646px;
	height: auto;
}

.global-apply-dialog .el-dialog__header {
  display: none;
}

.global-apply-dialog .el-dialog__body {
	width: 646px;
  height: auto;
  max-height: calc(100vh - 32px);
  padding: 0;
  border-radius: 20px;
}

.dialog-body {
	 width: 646px;
  max-height: calc(100vh - 32px);
  padding: 20px 69px;
  box-sizing: border-box;
  overflow-y: auto;
}

.dialog-title-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
}

.dialog-title-row h2 {
  margin: 0;
  font-weight: 600;
  font-size: 22px;
  color: #3D3D3D;
  line-height: 24px;
}

.sparkle {
	width: 39px;
	height: 33px;
}

.sparkle.left {
  transform: translateY(-4px);
}

.sparkle.right {
  transform: translateY(2px);
}

.apply-form {
  margin-top: 10px;
}

.apply-form .el-form-item {
  margin-bottom: 16px;
}

.apply-form .el-form-item:last-of-type {
  margin-bottom: 34px;
}

.apply-form .el-form-item__label {
  min-width: 68px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 0 14px;
  font-family: Source Han Sans, Source Han Sans;
  font-weight: 400;
  font-size: 14px;
  color: #1e293b;
  line-height: 20px;
  text-align: center;
  font-style: normal;
  text-transform: none;
}
.el-form-item.is-required:not(.is-no-asterisk)>.el-form-item__label:before {
   display: none !important;
}
.el-form-item.is-required:not(.is-no-asterisk)>.el-form-item__label:after {
    content: '*';
    color: #b58cff;
    margin-left: 4px;
	
}

.el-checkbox__label{
	font-weight: 400 !important;
	font-size: 15px !important;
	color: #3B3B3B !important;
}

.dialog-control,
.dialog-textarea {
  width: 508px;
  margin-top: 10px;
}

.dialog-control .el-input__inner,
.dialog-control .el-textarea__inner,
.dialog-textarea .el-textarea__inner {
  width: 508px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  color: #1e293b;
}

.dialog-control .el-input__inner {
  height: 45px;
  line-height: 45px;
}

.dialog-textarea .el-textarea__inner {
  height: 90px;
  min-height: 90px !important;
  padding-top: 12px;
  padding-bottom: 12px;
}

.dialog-control .el-input__inner::placeholder,
.dialog-textarea .el-textarea__inner::placeholder {
  color: #b3bdd3;
}

.captcha-row {
  width: 508px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 87px;
  gap: 10px;
  align-items: center;
}

.captcha-input,
.captcha-input .el-input__inner {
  width: 100%;
}

.captcha-box {
  width: 87px;
  height: 45px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  overflow: hidden;
  cursor: pointer;
  color: #64748b;
  margin-top: 8px;
}

.captcha-image {
  width: 100%;
  height: 100%;
  display: block;
}

.demand-group {
  width: 508px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding-top: 2px;
}

.demand-group .el-checkbox {
  margin-right: 0;
  color: #3a4560;
  font-size: 14px;
}

.demand-group .el-checkbox__inner {
  width: 16px;
  height: 16px;
  border-radius: 2px;
  border-color: #d7dfef;
}

.demand-group .el-checkbox__input.is-checked .el-checkbox__inner,
.demand-group .el-checkbox__input.is-indeterminate .el-checkbox__inner {
  background: #7b63f6;
  border-color: #7b63f6;
}

.submit-btn {
  display: block;
  width: 208px;
  height: 52px;
  margin: 24px auto 0;
  border: 0;
  border-radius: 14px;
  background: linear-gradient(90deg, #5f66ec 0%, #9557f6 100%);
  box-shadow: 0 8px 24px rgba(120, 99, 246, 0.24);
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
}

.submit-btn:disabled {
  cursor: not-allowed;
  opacity: 0.75;
}

@media (max-width: 760px) {
  .global-apply-dialog {
    width: calc(100vw - 24px);
    height: auto;
  }

  .dialog-body {
    height: auto;
    padding: 36px 20px 28px;
  }

  .dialog-control,
  .dialog-textarea,
  .captcha-row,
  .dialog-control .el-input__inner,
  .dialog-textarea .el-textarea__inner,
  .demand-group {
    width: 100%;
  }
}
</style>
